import type { MessageInstance } from 'antd/es/message/interface';
import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { WebSocketEventRouter } from '@/shared/websocket/router';
import type { WebSocketEnvelope } from '@/shared/websocket/types';

// Схемы для валидации событий модулей
const ModuleStartedSchema = z.object({
  module_name: z.string(),
  schedule_id: z.number(),
  history_id: z.number(),
  started_at: z.number(),
});

const ModuleFinishedSchema = z.object({
  module_name: z.string(),
  schedule_id: z.number(),
  history_id: z.number(),
  status: z.enum(['success', 'error']),
  execution_time_ms: z.number().optional(),
  error_message: z.string().optional(),
  finished_at: z.number(),
});

const ModuleStatusUpdatedSchema = z.object({
  module_name: z.string(),
  schedule_id: z.number(),
  last_run_status: z.string().optional(),
  last_run_error: z.string().optional(),
  run_count: z.number().optional(),
  date_last_run: z.number().optional(),
  date_next_run: z.number().optional(),
});

export interface ModuleEventsDependencies {
  queryClient: QueryClient;
  translate: (key: string, params?: Record<string, unknown>) => string;
  message: MessageInstance;
}

/**
 * Регистрирует обработчики WebSocket событий для модулей
 */
export function registerModuleWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient, translate, message }: ModuleEventsDependencies,
): () => void {
  // Обработчик события запуска модуля
  const handleModuleStarted = (event: WebSocketEnvelope) => {
    const result = ModuleStartedSchema.safeParse(event.data);
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.warn('[ModuleEvents] Невалидные данные module.started', event.data, result.error);
      }
      return;
    }

    const { module_name, started_at } = result.data;

    // Инвалидировать кеш расписаний для обновления статуса
    queryClient.invalidateQueries({ queryKey: ['modules', 'schedules'] });
    queryClient.invalidateQueries({ queryKey: ['modules', module_name, 'history'] });

    if (import.meta.env.DEV) {
      console.log('[ModuleEvents] Модуль запущен', module_name, new Date(started_at * 1000));
    }
  };

  // Обработчик события завершения модуля
  const handleModuleFinished = (event: WebSocketEnvelope) => {
    try {
      if (!event || typeof event !== 'object' || !('data' in event)) {
        if (import.meta.env.DEV) {
          console.warn('[ModuleEvents] Пустое или некорректное событие module.finished', event);
        }
        return;
      }

      const parseResult = ModuleFinishedSchema.safeParse(event.data ?? {});
      if (!parseResult || typeof parseResult !== 'object' || typeof (parseResult as any).success !== 'boolean') {
        if (import.meta.env.DEV) {
          console.warn('[ModuleEvents] Некорректный parseResult module.finished', event.data, parseResult);
        }
        return;
      }

      if (!parseResult.success) {
        if (import.meta.env.DEV) {
          console.warn('[ModuleEvents] Невалидные данные module.finished', event.data, parseResult?.error);
        }
        return;
      }

      const { module_name, status, error_message, execution_time_ms } = parseResult.data;

      // Инвалидировать кеш расписаний и истории
      queryClient.invalidateQueries({ queryKey: ['modules', 'schedules'] });
      queryClient.invalidateQueries({ queryKey: ['modules', module_name, 'history'] });

      // Показать уведомление
      if (status === 'error') {
        message.error(
          translate('modules:notifications.module_finished_error', {
            module: module_name,
            error: error_message || translate('modules:notifications.unknown_error'),
          }),
          5,
        );
      } else {
        const duration = execution_time_ms ? ` ${(execution_time_ms / 1000).toFixed(1)}с` : '';
        message.success(
          translate('modules:notifications.module_finished_success', {
            module: module_name,
            duration,
          }),
          3,
        );
      }

      if (import.meta.env.DEV) {
        console.log('[ModuleEvents] Модуль завершен', module_name, status, {
          execution_time_ms,
          error_message,
        });
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[ModuleEvents] Необработанное исключение module.finished', event, error);
      }
    }
  };

  // Обработчик события обновления статуса модуля
  const handleModuleStatusUpdated = (event: WebSocketEnvelope) => {
    const result = ModuleStatusUpdatedSchema.safeParse(event.data);
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.warn('[ModuleEvents] Невалидные данные module.status_updated', event.data, result.error);
      }
      return;
    }

    const { module_name } = result.data;

    // Инвалидировать кеш расписаний для обновления статуса
    queryClient.invalidateQueries({ queryKey: ['modules', 'schedules'] });

    if (import.meta.env.DEV) {
      console.log('[ModuleEvents] Статус модуля обновлен', module_name);
    }
  };

  // Регистрация обработчиков
  const disposers = [
    router.on('module.started', handleModuleStarted),
    router.on('module.finished', handleModuleFinished),
    router.on('module.status_updated', handleModuleStatusUpdated),
  ];

  return () => {
    disposers.forEach((dispose) => dispose());
  };
}

